import * as React from 'react';

//PACKAGES
import { useHistory } from 'react-router-dom';

// API
import apibridge from '../apibridge';

// UTILS
import { validationErrorsToString } from '../utils';

// REDUX
import UserSlice from '../store/slices/User';
import { useAppDispatch } from '../store';

// COMPONENTS
import Button from './Button';
import ErrorMessage from './ErrorMessage';
import GlobalModal from './GlobalModal';
import RemoveModal from '../pages/MyAccount/Modals/RemoveModal';
import OrganisationLogo from './OrganisationLogo';

// ICONS
import { ReactComponent as PlusIcon } from '../assets/svg/plus.svg';

// TYPES
import { AccountMyAccountOrganisation, DomainParentVolunteerStatus } from '../api/models';

interface MyAccountOrganisationsTypes {
	organisations?: Array<AccountMyAccountOrganisation>;
	defaultMaxItems?: number;
	setRequestVolunteerModalShow: React.Dispatch<React.SetStateAction<boolean>>;
	update: () => Promise<void>;
	setOrganisationId: React.Dispatch<React.SetStateAction<string>>;
}

const MyAccountOrganisations: React.FC<MyAccountOrganisationsTypes> = ({
	organisations,
	defaultMaxItems,
	setRequestVolunteerModalShow,
	update,
	setOrganisationId
}) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const [maxOrganisationsItem, setMaxOrganisationsItem] = React.useState<number>(defaultMaxItems || 5);

	const [volunteerId, setVolunteerId] = React.useState<string>('');

	const [volunteerStatus, setVolunteerStatus] = React.useState<string>('');

	const [isRemoveModalOpen, setIsRemoveModalOpen] = React.useState<boolean>(false);

	const history = useHistory();

	const dispatch = useAppDispatch();

	const changeProfile = async (profileId: string) => {
		setIsLoading(true);

		const response = await apibridge.doSwitchOrganisation(profileId);

		response.result = response.result || [];

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			dispatch(UserSlice.actions.forceUpdate({ profileId }));
			history.push('/dashboard');
		}
	};

	const removeVolunteerStatus = async (id: string) => {
		setIsRemoveModalOpen(true);

		setVolunteerStatus('Remove');

		setVolunteerId(id);
	};

	const widthdrawVolunteerStatus = async (id: string) => {
		setIsRemoveModalOpen(true);

		setVolunteerStatus('Withdraw');

		setVolunteerId(id);
	};

	const requestVolunteerStatus = async (id: string) => {
		setRequestVolunteerModalShow(true);

		setOrganisationId(id);
	};

	return (
		<>
			<section className="info-section my-account-organisations">
				<div className="section-header">
					<h2 className="header-name color-dark">Organisations</h2>
				</div>

				{organisations &&
					organisations.length > 0 &&
					organisations.slice(0, maxOrganisationsItem).map((item, index) => {
						return (
							<div key={index} className="row-container">
								<div className="row detail-row">
									<div className="col-auto pdl-0 pdr-0">
										<OrganisationLogo src={item.imageUrl} size={40} />
									</div>

									<div className="col-md-auto full-width">
										<div className="row detail-info">
											<div className="col-lg-4 detail-name">{item.name}</div>

											<div className="col-lg-4 detail-status">
												<div className="title">{item.isCurrentMember ? '' : 'Past'} Role</div>

												<div className="name">{item.role}</div>
											</div>

											<div className="col-lg-4 detail-status">
												<div
													className={
														item.volunteerStatus === DomainParentVolunteerStatus.Approved
															? ' alert-success-text status mr-0'
															: item.volunteerStatus === DomainParentVolunteerStatus.Requested
															? ' alert-warning-text status mr-0'
															: 'status mr-0'
													}
												>
													{item.volunteerStatus === DomainParentVolunteerStatus.Approved
														? 'Approved Volunteer'
														: item.volunteerStatus === DomainParentVolunteerStatus.Requested
														? 'Volunteer Status Pending'
														: ''}
												</div>
											</div>
										</div>
									</div>

									<div className="col-auto pdr-0 link-group">
										{item.isEligibleVolunteer ? (
											item.volunteerStatus === DomainParentVolunteerStatus.Approved ? (
												<Button
													type="button"
													onClick={() => removeVolunteerStatus(item.userProfileId || '')}
													className="btn-text pdr-0 "
												>
													Remove Volunteer Status
												</Button>
											) : item.volunteerStatus === DomainParentVolunteerStatus.Requested ? (
												<Button
													type="button"
													onClick={() => widthdrawVolunteerStatus(item.userProfileId || '')}
													className="btn-text pdr-0 alert-danger-text"
												>
													Withdraw Volunteer Request
												</Button>
											) : (
												<Button
													type="button"
													onClick={() => requestVolunteerStatus(item.userProfileId || '')}
													className="btn-text pdr-0 "
												>
													Request Volunteer Status
												</Button>
											)
										) : item.isCurrentMember ? (
											<Button
												className="btn-text pdr-0 "
												onClick={() => {
													changeProfile(item.userProfileId!);
												}}
											>
												Dashboard
											</Button>
										) : null}
									</div>
								</div>
							</div>
						);
					})}

				{organisations && organisations.length > 0 && maxOrganisationsItem < organisations.length && (
					<Button
						className="btn-secondary btn-show-more"
						onClick={() => setMaxOrganisationsItem(organisations.length)}
						buttonIcon={<PlusIcon className="link-icon" />}
					>
						Show {organisations.length - maxOrganisationsItem} more
					</Button>
				)}

				{!!formErrorMessage && <ErrorMessage message={formErrorMessage} />}

				<GlobalModal
					titleText={`${volunteerStatus} volunteer status`}
					isOpen={isRemoveModalOpen}
					setIsOpen={setIsRemoveModalOpen}
				>
					<RemoveModal userProfileId={volunteerId} update={update} volunteerStatus={volunteerStatus} />
				</GlobalModal>
			</section>
		</>
	);
};

export default MyAccountOrganisations;
